import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import { BusinessStatusChip, businessStatus, CompanyTypeChip } from '../../../components/dashboard/BusinessChips';
import { formatDate, formatTaxId } from '../../../utils/formatters';

// Per-record flags computed by the backend (BusinessPolicy), same pattern as
// the leads' `authorization.capabilities` (see leadTransform.js).
export const businessCapabilities = (business) => {
  const capabilities = business?.authorization?.capabilities ?? {};
  return {
    canEdit: Boolean(capabilities.can_edit),
    canDelete: Boolean(capabilities.can_delete),
    canRestore: Boolean(capabilities.can_restore),
  };
};

// "o cliente X, Loja A, Loja B" — who else uses this company data.
export const sharedNames = (t, sharedWith) =>
  [
    sharedWith?.customer && t('businesses.sharedWithCustomer', { name: sharedWith.customer.name }),
    ...(sharedWith?.businesses ?? []).map((business) => business.name),
  ]
    .filter(Boolean)
    .join(', ');


// Row actions shared by the DataGrid kebab and the mobile list.
export const buildBusinessActions = ({ t, business, onView, onEdit, onDelete, onRestore }) => {
  const { canEdit, canDelete, canRestore } = businessCapabilities(business);
  const actions = [
    {
      label: t('businesses.actions.view'),
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: () => onView(business),
    },
  ];
  if (canRestore) {
    actions.push({
      label: t('businesses.actions.restore'),
      icon: <RestoreIcon fontSize="small" />,
      onClick: () => onRestore(business),
    });
  }
  if (canEdit && !business.is_deleted) {
    actions.push({
      label: t('businesses.actions.edit'),
      icon: <EditOutlinedIcon fontSize="small" />,
      onClick: () => onEdit(business),
    });
  }
  if (canDelete) {
    actions.push({
      label: t('businesses.actions.delete'),
      icon: <DeleteOutlineIcon fontSize="small" />,
      color: 'error',
      onClick: () => onDelete(business),
    });
  }
  return actions;
};

// With `getRowHeight="auto"` (needed so the two-line cells don't clip in
// compact density), DataGrid stops vertically centering cell content —
// wrapping single-line cells in this restores the centering.
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

export const buildBusinessColumns = ({ t, language, isInternal, ...actionHandlers }) => [
  {
    field: 'display_name',
    headerName: t('businesses.fields.displayName'),
    flex: 1,
    minWidth: 200,
    resizable: false,
    renderCell: (params) => (
      <Stack spacing={0} sx={{ py: 1 }}>
        <Link component={RouterLink} to={`/businesses/${params.row.uuid}`} variant="body2" underline="hover">
          {params.row.display_name}
        </Link>
        {params.row.legal_name && (
          <Typography variant="caption" color="text.secondary">
            {params.row.legal_name}
          </Typography>
        )}
      </Stack>
    ),
  },
  // Internal users manage every customer's stores; collaborators only see
  // their own customer's, so the column would be noise for them.
  ...(isInternal
    ? [
        {
          field: 'customer_name',
          headerName: t('businesses.fields.customer'),
          width: 180,
          renderCell: (params) => (
            <CenteredCell>
              <Link component={RouterLink} to={`/customers/${params.row.customer_uuid}`} variant="body2" underline="hover">
                {params.value}
              </Link>
            </CenteredCell>
          ),
        },
      ]
    : []),
  {
    field: 'tax_id',
    headerName: t('businesses.fields.taxId'),
    width: 170,
    renderCell: (params) => <CenteredCell>{formatTaxId(params.value, params.row.country) || '—'}</CenteredCell>,
  },
  {
    field: 'company_type',
    headerName: t('businesses.fields.companyType'),
    width: 170,
    valueGetter: (value) => (value ? t(`businesses.companyTypes.${value}`) : ''),
    renderCell: (params) => (
      <Stack spacing={0.25} justifyContent="center" sx={{ py: 1, height: '100%' }}>
        <Box>
          <CompanyTypeChip t={t} companyType={params.row.company_type} />
        </Box>
        {params.row.parent_company_name && (
          <Typography variant="caption" color="text.secondary">
            {params.row.parent_company_name}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    field: 'status',
    headerName: t('businesses.fields.status'),
    width: 120,
    valueGetter: (value, row) => t(`businesses.statuses.${businessStatus(row)}`),
    renderCell: (params) => (
      <CenteredCell>
        <BusinessStatusChip t={t} business={params.row} />
      </CenteredCell>
    ),
  },
  {
    field: 'created_at',
    headerName: t('businesses.fields.createdAt'),
    width: 130,
    type: 'dateTime',
    valueGetter: (value) => (value ? new Date(value) : null),
    renderCell: (params) => <CenteredCell>{formatDate(params.row.created_at, language)}</CenteredCell>,
  },
  {
    field: 'rowActions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu actions={buildBusinessActions({ t, business: params.row, ...actionHandlers })} />
      </CenteredCell>
    ),
  },
];
